/**
 * THE PAID-COPY MONEY-MOVING GATE — hard, server-side, cannot be bypassed.
 *
 * Incident, 2026-09-02/03: an ad reached Meta TWICE with primary text
 * containing rhetorical flourish and multiple em dashes ("Mora reads your
 * website first — archetype, voice, palette, pillars — so the first draft
 * already sounds like you. ... look at the brand kit it builds from your
 * site and judge the drafts yourself."). The repo has had an anti-slop
 * module since 2026-08-21 (`slop.ts`) plus `SLOP_VOCABULARY_PATTERN`
 * (`ad-contract.ts`) — and NOTHING in the paid execution path called either.
 * This file is the fix: a pure check wired directly into the money-moving
 * seam (`prepare_execution_v3` in
 * `supabase/functions/manage-paid-media-proposals/v3-execution-boundary.ts`),
 * not the client, so no UI or agent can skip it.
 *
 * ── WHY THIS FILE CANNOT IMPORT `ad-contract.ts` ────────────────────────────
 * This module is imported by an edge function bundled WITHOUT an import map,
 * so nothing in its import chain may use a bare/path-aliased specifier.
 * `slop.ts` has zero imports of its own (regex/string only — verified by
 * reading the file), so it is safe. `ad-contract.ts` imports
 * `@/constants/ad-specs` and `@/lib/briefs/ad-schemas` — both path-aliased,
 * i.e. bare, specifiers a Deno bundle without an import map cannot resolve —
 * so it CANNOT be imported here, directly or transitively.
 *
 * The one thing this file needs from `ad-contract.ts` is its
 * `SLOP_VOCABULARY_PATTERN` regex. Rather than import the file (and its bare
 * imports along with it), the regex is RE-DECLARED below, verbatim, with a
 * comment naming the source line. `paid-copy-slop-gate.test.ts` reads both
 * files' source at test time and asserts the two regex literals stay
 * byte-identical, so this copy cannot silently drift from the original.
 *
 * ── SEVERITY DISCIPLINE (content-doctrine/README.md) ────────────────────────
 * Every rule here is objective and countable — a character count, a regex
 * hit, a literal fragment — never taste. That is what allows `blocked` to be
 * a hard fail instead of a warning nobody reads.
 */

import { AD_SPECS } from '../../constants/ad-specs.ts';

import { checkProseSlop, quoteAround } from './slop.ts';

/**
 * `AD_SPECS` (`src/constants/ad-specs.ts`) is imported the same way `slop.ts`
 * is — a relative, extensioned specifier — for the same reason stated in the
 * file header above: this module ships inside a Deno bundle with no import
 * map, so a bare/path-aliased specifier (`@/constants/ad-specs`) would fail
 * to resolve. `ad-specs.ts` has zero imports of its own (verified by reading
 * the file), so, like `slop.ts`, it is safe to pull in directly rather than
 * re-declaring its numbers a second time.
 */
export type PaidCopySlopField = 'primaryText' | 'headline' | 'description';

export interface PaidCopySlopFinding {
  field: PaidCopySlopField;
  rule: string;
  /** What the reader would call it — `slop.ts`'s own `label` for a
   *  delegated finding, one authored line per rule this file owns directly. */
  label: string;
  /** The concrete instruction to fix it, phrased as an imperative next move —
   *  `slop.ts`'s own `fix` for a delegated finding, one authored line per
   *  rule this file owns directly. This is what a Review row's title reads,
   *  the same grammar every sibling structural issue already uses. */
  fix: string;
  /** The offending fragment or count, quotable directly in a refusal message. */
  evidence: string;
}

export interface PaidCopySlopReport {
  blocked: boolean;
  findings: PaidCopySlopFinding[];
}

export interface PaidCopySlopInput {
  primaryText: string | null | undefined;
  headline: string | null | undefined;
  description: string | null | undefined;
}

/**
 * RE-DECLARED from `src/lib/content-doctrine/ad-claim-vocabulary.ts` (the
 * `SLOP_VOCABULARY_PATTERN` constant, defined directly above
 * `UNSUPPORTED_PERFORMANCE_OUTCOME_PATTERN`). `ad-contract.ts` imports it from there. Kept identical on purpose — see
 * the file-header comment above and `paid-copy-slop-gate.test.ts`, which
 * proves the two never drift by reading both files' source directly.
 */
const SLOP_VOCABULARY_PATTERN =
  /\b(?:game[- ]?chang(?:er|ing)|revolutionary|cutting[- ]edge|state[- ]of[- ]the[- ]art|world[- ]class|best[- ]in[- ]class|next[- ]level|supercharge|unlock the (?:power|secret)|elevate your|unleash)\b/i;

/**
 * The CLAUDE.md-banned marketing filler that asserts a value statement about
 * the product itself, rather than describing what it does — "🔴 Product
 * language (2026-08-26)": magic, autopilot, "AI-powered" as a value
 * statement.
 */
const BANNED_MARKETING_WORDS_PATTERN = /\b(?:AI[- ]powered|magic(?:al)?|autopilot)\b/i;

/**
 * A paired em-dash span whose middle contains a comma — the incident's exact
 * shape: "first — archetype, voice, palette, pillars — so the first draft".
 * Distinct from the plain em-dash-budget check below: that one flags ANY em
 * dash anywhere in the field, this one names the specific "dashes bracketing
 * a list" construction so the refusal message can quote it precisely.
 */
const EM_DASH_LIST_CONSTRUCTION_PATTERN = /—[^—]*,[^—]*—/;

/** "look at the brand kit ...", "look at how ..." — an imperative pointed at the reader. */
const LOOK_AT_IMPERATIVE_PATTERN = /\blook at\b/i;

/** "judge the drafts yourself", "judge it for yourself" — same shape, different verb. */
const JUDGE_YOURSELF_IMPERATIVE_PATTERN = /\bjudge\b[^.!?]{0,80}\byourself\b/i;

/**
 * Text that belongs to Mora's own screens, not to an ad: an instruction to the
 * person using Mora ("Approve the concept first") or a placeholder left in the
 * field ("[Headline here]", "{{product}}", "lorem ipsum"). Walk 2, 2026-10-21:
 * a headline that told the marketer what to do next was saved as Plan 1's
 * headline and would have been sent to Meta. Objective: a verb aimed at the
 * person using Mora plus a noun that only exists inside Mora, or a template
 * marker. Applies to the headline and description only; primary text can
 * describe the product's own review step.
 */
const INTERFACE_INSTRUCTION_PATTERN =
  /^\s*(?:please\s+)?(?:(?:approve|review|confirm|check|edit|select|choose|pick|click|press|tap|add|insert|fill in|enter|replace|write|save|upload)\b[^.!?]{0,60}\b(?:the concept first|this concept|your brief|this field|this step|the copy first)\b|approve the (?:concept|brief|draft)\b|make this the current\b)/i;

const PLACEHOLDER_PATTERN =
  /\[[^\]]{0,40}\b(?:headline|name|product|insert|brand|cta|text)\b[^\]]{0,40}\]|\{\{[^}]*\}\}|<[a-z][a-z _-]{0,30}>|\blorem ipsum\b|\b(?:headline|primary text|description|your text|insert text|copy) goes here\b|\b(?:headline|primary text|description|insert text) here\b|\btbd\b|\bxxx+\b/i;

const PLACEHOLDER_ALL_CAPS_PATTERN = /\[[A-Z][A-Z0-9 _-]{1,40}\]/;

/** The first fragment of `text` that is interface text or a placeholder, or null. */
export function findInterfaceTextInCopy(
  field: PaidCopySlopField,
  text: string,
): { rule: 'interface_instruction' | 'placeholder_text'; evidence: string } | null {
  if (field !== 'primaryText') {
    const instruction = INTERFACE_INSTRUCTION_PATTERN.exec(text);
    if (instruction) return { rule: 'interface_instruction', evidence: quote(instruction[0]) };
  }
  const placeholder = PLACEHOLDER_PATTERN.exec(text) ?? PLACEHOLDER_ALL_CAPS_PATTERN.exec(text);
  if (placeholder) return { rule: 'placeholder_text', evidence: quote(placeholder[0]) };
  return null;
}

/**
 * Sourced from `AD_SPECS.meta.*.adviseMax` — never a second, independently
 * typed 40/30/125 — so the gate and every other reader of the same numbers
 * (`ad-readiness.ts`'s `corny_hook_too_long`/`corny_description_too_long`,
 * the Ad step's field descriptions) can never drift apart. Exported so a
 * caller that needs to render the number (a field counter, an advisory note)
 * has one constant to import instead of retyping the literal.
 * `AD_SPECS.meta.headline.adviseMax` is not currently null, but the `?? `
 * fallback keeps this file compiling honestly against the field's real
 * `number | null` type rather than a non-null assertion.
 */
export const HEADLINE_MAX_CHARS = AD_SPECS.meta.headline.adviseMax ?? 40;
export const DESCRIPTION_MAX_CHARS = AD_SPECS.meta.description.adviseMax ?? 30;
export const HOOK_MAX_CHARS = AD_SPECS.meta.hookFold.adviseMax ?? 125;

function quote(text: string, max = 140): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}

/**
 * The first sentence of `text` — the "hook" a reader sees before the fold.
 * Ends at the first `.`/`!`/`?` followed by whitespace or end-of-string; if
 * no terminal punctuation exists, the whole (trimmed) text is the hook.
 */
export function extractHook(text: string): string {
  const trimmed = text.trim();
  const match = /[.!?](?:\s|$)/.exec(trimmed);
  if (!match) return trimmed;
  return trimmed.slice(0, match.index + 1);
}

/**
 * The four content-shape rules (em dash / stock slop, banned marketing
 * vocabulary, banned CLAUDE.md words, rhetorical imperatives) that apply
 * identically to every provider's paid copy — Meta primary text/headline/
 * description AND Google RSA headlines/descriptions share this exact
 * engine. Only the field-specific LENGTH ceilings differ per provider (Meta's
 * `checkField` below applies Meta's `AD_SPECS.meta.*` numbers plus the
 * primary-text-only hook rules; {@link checkGoogleRsaCopySlop} applies
 * `AD_SPECS.google_rsa.*` instead) — extracted here so neither provider's
 * length numbers can leak into the other's check by accident.
 */
function checkFieldContentRules(field: PaidCopySlopField, text: string): PaidCopySlopFinding[] {
  const findings: PaidCopySlopFinding[] = [];

  // 0. A line written for the person using Mora, or a placeholder left in.
  const interfaceText = findInterfaceTextInCopy(field, text);
  if (interfaceText) {
    findings.push({
      field,
      rule: interfaceText.rule,
      label:
        interfaceText.rule === 'interface_instruction'
          ? 'Instruction to the person using Mora'
          : 'Placeholder text',
      fix:
        interfaceText.rule === 'interface_instruction'
          ? 'Write a line for the customer who sees the ad, not a step for the person making it.'
          : 'Replace the placeholder with the real line.',
      evidence: interfaceText.evidence,
    });
  }

  // 1. Em dashes: zero allowed on every paid-copy field, no exceptions. Also
  //    surfaces slop.ts's own SLOP_RULES (stock vocabulary, throat-clearing,
  //    colon reveals, etc.) as a free extra layer — they are already
  //    established countable rules elsewhere in this module. `label`/`fix`
  //    are slop.ts's own, passed through verbatim — never re-authored here.
  const prose = checkProseSlop(text, { emDashBudget: 0 });
  for (const finding of prose.findings) {
    findings.push({
      field,
      rule: finding.ruleId === 'em_dash' ? 'em_dash' : `slop:${finding.ruleId}`,
      label: finding.label,
      fix: finding.fix,
      evidence: finding.quote,
    });
  }

  // 2. SLOP_VOCABULARY hits (re-declared from ad-contract.ts — see header).
  const vocabMatch = SLOP_VOCABULARY_PATTERN.exec(text);
  if (vocabMatch) {
    findings.push({
      field,
      rule: 'slop_vocabulary',
      label: 'Stock marketing phrase',
      fix: 'Name the specific outcome instead of the buzzword.',
      evidence: quote(vocabMatch[0]),
    });
  }

  // 3. CLAUDE.md-banned marketing words.
  const bannedMatch = BANNED_MARKETING_WORDS_PATTERN.exec(text);
  if (bannedMatch) {
    findings.push({
      field,
      rule: 'banned_marketing_word',
      label: 'Banned product-value word',
      fix: 'Describe what the product does instead of asserting its value.',
      evidence: quote(bannedMatch[0]),
    });
  }

  // 4. Rhetorical imperatives addressed to the reader. `rhetorical_look_at`
  //    uses `quoteAround` (not the plain slice+`quote()` this used before)
  //    so a window that truncates mid-word on either side always carries an
  //    ellipsis marker instead of reading as an accidental sentence
  //    fragment — `judgeMatch[0]`/`match[0]` below are the regex's own
  //    complete match, never a padded window, so they need no such fix.
  const lookAtMatch = LOOK_AT_IMPERATIVE_PATTERN.exec(text);
  if (lookAtMatch) {
    findings.push({
      field,
      rule: 'rhetorical_look_at',
      label: 'Rhetorical "look at"',
      fix: 'State the fact directly instead of instructing the reader to look at something.',
      evidence: quoteAround(text, lookAtMatch.index, lookAtMatch[0].length),
    });
  }
  const judgeMatch = JUDGE_YOURSELF_IMPERATIVE_PATTERN.exec(text);
  if (judgeMatch) {
    findings.push({
      field,
      rule: 'rhetorical_judge_yourself',
      label: 'Rhetorical "judge yourself"',
      fix: 'State the claim directly instead of inviting the reader to judge it themselves.',
      evidence: quote(judgeMatch[0]),
    });
  }

  return findings;
}

function checkField(
  field: PaidCopySlopField,
  rawText: string | null | undefined,
): PaidCopySlopFinding[] {
  const text = (rawText ?? '').trim();
  if (!text) return [];

  const findings: PaidCopySlopFinding[] = [...checkFieldContentRules(field, text)];

  // 5. Field-specific character ceilings. `ad-specs.ts`'s own provenance
  //    marks these "they-assert" truncation guidance, not a Meta API
  //    rejection — the authoring surface (`meta-authoring-view-model.ts`)
  //    routes these three rules to its advisory Notes channel rather than a
  //    blocking issue, the same discipline `ad-readiness.ts` already applies
  //    to the identical numbers under its own `corny_hook_too_long` /
  //    `corny_description_too_long` checks.
  if (field === 'headline' && text.length > HEADLINE_MAX_CHARS) {
    findings.push({
      field,
      rule: 'headline_length',
      label: 'Headline runs long',
      fix: 'Meta may truncate display before this point; trim for the on-screen glance.',
      evidence: `${text.length} characters, over the ${HEADLINE_MAX_CHARS}-character cap: "${quote(text)}"`,
    });
  }
  if (field === 'description' && text.length > DESCRIPTION_MAX_CHARS) {
    findings.push({
      field,
      rule: 'description_length',
      label: 'Description runs long',
      fix: 'Meta may truncate display before this point; keep it as a short bonus line.',
      evidence: `${text.length} characters, over the ${DESCRIPTION_MAX_CHARS}-character cap: "${quote(text)}"`,
    });
  }

  // 6. Hook-specific rules — the first sentence only, primary text alone.
  if (field === 'primaryText') {
    const hook = extractHook(text);
    if (hook.length > HOOK_MAX_CHARS) {
      findings.push({
        field,
        rule: 'hook_length',
        label: 'Hook runs long',
        fix: 'Cut the hook to land before the fold; move the rest into the body.',
        evidence: `Hook is ${hook.length} characters, over the ${HOOK_MAX_CHARS}-character cap: "${quote(hook)}"`,
      });
    }
    if (EM_DASH_LIST_CONSTRUCTION_PATTERN.test(hook)) {
      const match = EM_DASH_LIST_CONSTRUCTION_PATTERN.exec(hook);
      findings.push({
        field,
        rule: 'em_dash_list_in_hook',
        label: 'Em-dash list in the hook',
        fix: 'Rewrite the hook as a plain sentence without dash-bracketed asides.',
        evidence: `Em-dash list construction in the hook: "${quote(match ? match[0] : hook)}"`,
      });
    }
  }

  return findings;
}

/**
 * The hard gate. Every rule here is objective (character count, regex hit),
 * so `blocked` is a real refusal, never a warning — see the severity
 * discipline in `content-doctrine/README.md`.
 */
export function checkPaidCopySlop(input: PaidCopySlopInput): PaidCopySlopReport {
  const findings: PaidCopySlopFinding[] = [
    ...checkField('primaryText', input.primaryText),
    ...checkField('headline', input.headline),
    ...checkField('description', input.description),
  ];
  return {
    blocked: findings.length > 0,
    findings,
  };
}

/**
 * ── THE GOOGLE RSA ENTRY POINT ──────────────────────────────────────────
 * A Google responsive search Ad has no `primaryText`/hook concept — only
 * headline and description text assets, each capped by Google's own hard
 * maxima (`AD_SPECS.google_rsa.headline.hardMax` 30 / `.description.hardMax`
 * 90 — verified platform-doc, not they-assert). Those hard maxima are
 * ALREADY enforced as a blocking issue elsewhere (`googleRsaReadyIssues` in
 * `src/lib/paid-media/google/options.ts`, `rsa_headline_too_long` /
 * `rsa_description_too_long`) — this gate's OWN length findings below reuse
 * `AD_SPECS.google_rsa.*.adviseMax` (identical numbers today) only so a
 * caller has one place to read "how long is too long," and are treated
 * advisory by every UI caller (`google-search-authoring-view-model.ts`
 * routes `headline_length`/`description_length` to its Notes channel, the
 * same discipline `META_COPY_SLOP_ADVISORY_RULES` already applies) precisely
 * to avoid a second, differently-worded blocking issue for the same fact.
 * The content-shape rules (em dash, banned vocabulary, banned words,
 * rhetorical imperatives) are the SAME `checkFieldContentRules` engine Meta
 * uses — never a second, independently authored copy.
 */
export interface GoogleRsaCopySlopInput {
  headline: string | null | undefined;
  description: string | null | undefined;
}

export const GOOGLE_RSA_HEADLINE_MAX_CHARS = AD_SPECS.google_rsa.headline.adviseMax ?? 30;
export const GOOGLE_RSA_DESCRIPTION_MAX_CHARS = AD_SPECS.google_rsa.description.adviseMax ?? 90;

function checkGoogleField(
  field: Extract<PaidCopySlopField, 'headline' | 'description'>,
  rawText: string | null | undefined,
): PaidCopySlopFinding[] {
  const text = (rawText ?? '').trim();
  if (!text) return [];

  const findings: PaidCopySlopFinding[] = [...checkFieldContentRules(field, text)];

  if (field === 'headline' && text.length > GOOGLE_RSA_HEADLINE_MAX_CHARS) {
    findings.push({
      field,
      rule: 'headline_length',
      label: 'Headline runs long',
      fix: 'Google may truncate or reject headline text past this point; trim to fit the character limit.',
      evidence: `${text.length} characters, over the ${GOOGLE_RSA_HEADLINE_MAX_CHARS}-character cap: "${quote(text)}"`,
    });
  }
  if (field === 'description' && text.length > GOOGLE_RSA_DESCRIPTION_MAX_CHARS) {
    findings.push({
      field,
      rule: 'description_length',
      label: 'Description runs long',
      fix: 'Google may truncate or reject description text past this point; trim to fit the character limit.',
      evidence: `${text.length} characters, over the ${GOOGLE_RSA_DESCRIPTION_MAX_CHARS}-character cap: "${quote(text)}"`,
    });
  }

  return findings;
}

/**
 * The Google-RSA counterpart to {@link checkPaidCopySlop} — same shape
 * (`blocked`/`findings`), same content-rule engine, Google's own length
 * caps. `prepare_execution_v3` for `google-ads` calls this once per headline
 * and once per description text asset (see `v3-execution-boundary.ts`'s
 * `checkGoogleDraftCopySlop`), the same per-string looping
 * `checkMetaDraftCopySlop` already does for Meta's primary texts/headlines/
 * descriptions.
 */
export function checkGoogleRsaCopySlop(input: GoogleRsaCopySlopInput): PaidCopySlopReport {
  const findings: PaidCopySlopFinding[] = [
    ...checkGoogleField('headline', input.headline),
    ...checkGoogleField('description', input.description),
  ];
  return {
    blocked: findings.length > 0,
    findings,
  };
}

/**
 * Renders findings as one message per finding, suitable for a refusal body a
 * human or an agent can act on directly: field, rule, offending fragment.
 */
export function paidCopySlopFindingsAsMessages(findings: readonly PaidCopySlopFinding[]): string[] {
  return findings.map((f) => `[${f.field}] ${f.rule}: ${f.evidence}`);
}
